import { Link } from 'react-router-dom';
import { useDischargeAwaiting } from '../api/standing.ts';
import { LoadFailure } from '../components/LoadFailure.tsx';
import { formatDate } from '../dates.ts';

/**
 * Discharges awaiting confirmation (107). Only final accountings survive a
 * discharge; everything else the system raised closed on the discharge,
 * assumed met or waived, pending confirmation. Every discharged client is
 * listed here until a person confirms, by name and date, on the client's page.
 * Row security decides who is listed.
 */
export function DischargeConfirmation() {
  const list = useDischargeAwaiting();
  const rows = list.data?.awaiting;
  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-[96rem] lg:px-6 lg:py-6">
        <header className="page-head">
          <h1 className="page-title">Discharges awaiting confirmation</h1>
          <p className="mt-0.5 max-w-[60rem] text-sm text-neutral-500">
            Each discharge closed what it ended on an assumption: the obligations are assumed met or waived,
            pending confirmation. Final accountings stay open. Confirm on the client's page
            {list.data && !list.data.may_confirm ? ' (Compliance, or tiers 1 and 2)' : ''}.
          </p>
        </header>
        <div className="page-body">
          {list.error && !rows ? (
            <LoadFailure what="The discharges awaiting confirmation" error={list.error}
                         onRetry={() => void list.refetch()} retrying={list.isFetching} />
          ) : !rows ? (
            <p className="px-4 py-10 text-center text-neutral-600">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="px-4 py-10 text-center text-neutral-600">None. Every discharge has been confirmed.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="border-b border-neutral-200 text-xs font-bold tracking-[0.1em] text-neutral-500 uppercase">
                  <tr>
                    <th className="px-4 py-2 lg:px-5">Client</th>
                    <th className="px-3 py-2">Discharged</th>
                    <th className="px-3 py-2">Closed on the discharge</th>
                    <th className="px-3 py-2">Still open</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.client_id} className="border-b border-neutral-100 align-top" data-quick-client={r.client_id}>
                      <td className="px-4 py-2 lg:px-5">
                        <Link to={`/clients/${r.client_id}`} state={{ from: 'Discharges awaiting confirmation' }}
                              className="inline-flex min-h-[44px] items-center text-neutral-900 underline-offset-2 hover:underline">
                          {r.display_name}
                        </Link>
                        <span className="block text-sm text-neutral-500">{r.client_ref} · {r.legal_status}</span>
                      </td>
                      <td className="px-3 py-2 text-neutral-600">
                        {r.discharged_on ? formatDate(r.discharged_on) : 'Date not recorded'}
                      </td>
                      <td className="px-3 py-2 text-neutral-800">{r.closed_on_discharge}, assumed met or waived</td>
                      <td className="px-3 py-2 text-neutral-600">{r.still_open}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
